import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation, Outlet } from "react-router-dom";
import { Header } from "../../../../components/Header/Header";
import api from "../../../chat/api";
import toast from "react-hot-toast";
import TabLayout from "./TabLayout";
import UploadModal from "../../KbFolder/UploadFolderModal";
import GenerationLoadingModal from "../Proposal/GenerationLoadingModal";
import BidReadinessAnalysisModal from "./BidReadinessAnalysisModal";
import ConfirmDialog from "../../../../components/ConfirmDialog/ConfirmDialog";
import { FolderSvg } from "./Svgs";
import {
  startJob,
  getJob,
  wasCompleted,
  useFolderJobs,
} from "../../../../utils/analysisJobs";
import { Clock } from "lucide-react";
import ProjectActivityLog from "../../../../components/Notifications/ProjectActivityLog";
import HoverTooltip from "../../../../components/HoverTooltip";
import "./ProjectPage.css";

const ProjectPage = () => {
  const { projectId, orgId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Project Activity Log slide-out panel
  const [activityOpen, setActivityOpen] = useState(false);

  // --- STATE ---
  // FIX 1: Initialize additional_tasks in state
  const [files, setFiles] = useState({ documents: [], additional_tasks: [] });
  const [folderDetails, setFolderDetails] = useState({ name: "" });
  const [tableLoading, setTableLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const fileInputRef = useRef(null);

  const [tabUploadHandler, setTabUploadHandler] = useState(null);
  const [isTabBusy, setIsTabBusy] = useState(false);

  // Header & Generation States
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationPhase, setGenerationPhase] = useState(0);
  const [isGeneratingProposal, setIsGeneratingProposal] = useState(false);
  const [isAnsweringQuestionnaire, setIsAnsweringQuestionnaire] =
    useState(false);
  const [isFillingForms, setIsFillingForms] = useState(false);
  const [isCreateManageDropdownOpen, setIsCreateManageDropdownOpen] =
    useState(false);
  const createManageDropdownRef = useRef(null);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const [isPaidYearlyPlan, setIsPaidYearlyPlan] = useState(false);

  // Bid Readiness / Requirements "analyzing" state is derived from the analysis
  // job store (see below) so it survives navigation — no local flag here.
  const [isViewAnalysisOpen, setIsViewAnalysisOpen] = useState(false);
  const [analysisAnchor, setAnalysisAnchor] = useState({ top: 70, right: 16 });

  // Requirement alignment result (read-only here; the watcher drives the run).
  const [alignmentStatus, setAlignmentStatus] = useState("not_started"); // not_started | extraction_pending | running | done | failed
  const [alignmentResult, setAlignmentResult] = useState(null);

  // Bid Readiness is "ready" (show results instead of the Run Analysis prompt)
  // once a bid-readiness run has completed for this folder. `analysis_summary`
  // is the server-side signal: it is folder-scoped so it reaches collaborators,
  // and only the bid-readiness flow writes it — unlike the shared
  // `insights_status`, which a requirements extraction also sets to "completed"
  // and which would make Bid Readiness render an empty results page.
  // `wasCompleted` is a localStorage hint that only flips the browser that ran
  // the job, so it stays last and can never gate collaborators out.
  const hasDates = (files.documents || []).some(
    (d) => (d.insights?.dates || []).length > 0,
  );
  const analysisReady =
    !!files.analysis_summary ||
    hasDates ||
    wasCompleted(projectId, "bid-readiness");

  const currentPath = location.pathname.toLowerCase();
  const activeTab = currentPath.includes("bid-readiness")
    ? "bid-readiness"
    : currentPath.includes("requirements")
      ? "requirements"
      : "project-files";

  const handleTabChange = (tabPath) => {
    setTabUploadHandler(null);
    navigate(`/rfp-projects/project/${projectId}/org/${orgId}/${tabPath}`);
  };

  const showToast = useCallback((type, message) => {
    if (type === "error" || type === "Error") {
      toast.error(message);
    } else if (type === "warning" || type === "Warning") {
      toast(message, { icon: "⚠️" });
    } else {
      toast.success(message);
    }
  }, []);

  const fetchFiles = useCallback(
    async (isSilent = false) => {
      // If isSilent is passed as true, do not trigger the main UI loader
      if (!isSilent) setTableLoading(true);

      try {
        const filesUrl = orgId
          ? `api/v1/rfp/folders/${projectId}/files/?organization_id=${encodeURIComponent(orgId)}`
          : `api/v1/rfp/folders/${projectId}/files/`;
        const response = await api.get(filesUrl);
        if (response.status === 200) {
          setFiles({
            documents: response.data.documents || [],
            folder_name: response.data.folder_name,
            proposal_available: response.data.proposal_available,
            additional_tasks: response.data.additional_tasks || [],
            analysis_summary: response.data.analysis_summary, // IMPORTANT: make sure you grab this here!
            user_permission: response.data.user_permission, // gates reviewer-restricted actions (Create Proposal / Upload File)
            is_owner: response.data.is_owner,
            rfp_status: response.data.rfp_status,
          });
        }
      } catch (error) {
        const status = error.response?.status;
        const apiError = error.response?.data?.error;
        if ((status === 400 || status === 403) && apiError) {
          showToast("error", apiError);
        }
      } finally {
        if (!isSilent) setTableLoading(false);
      }
    },
    [projectId, orgId, showToast],
  );

  useEffect(() => {
    if (projectId) fetchFiles();
  }, [projectId, fetchFiles]);

  // Arriving from "Create Project" on a tender: the documents are still being
  // downloaded server-side when this page mounts. The import job is polled
  // from the first render (not after a first interval), its per-file states
  // feed the progress panel above the tabs, and the file list refreshes
  // silently as each document lands — no manual refresh, no blank page.
  // Captured once on mount: in-app redirects (the index route's <Navigate
  // replace>) rewrite location and drop its state, which would kill the poll
  // mid-import if this were read from location on every render.
  const [tenderImportId, setTenderImportId] = useState(() => location.state?.tenderImportId || null);
  const [importJob, setImportJob] = useState(null);
  const [retryingImport, setRetryingImport] = useState(false);
  const [importStalled, setImportStalled] = useState(false);
  // Set when an import finishes having brought in nothing. Holds the job's own
  // reason so the dialog can say which of the several causes it was.
  const [noDocsNotice, setNoDocsNotice] = useState(null);
  // Bumped to restart the poll after a retry, which puts a settled job back to
  // work and so needs watching again.
  const [importWatch, setImportWatch] = useState(0);
  const importDoneCountRef = useRef(-1);
  // True once this page has seen the job actually working, which is what makes
  // a done-placeholder meaningful rather than a ghost of a deleted file.
  const watchedImportRunningRef = useRef(false);

  // The state above only exists on the navigation that created the import, so
  // one refresh used to lose the job entirely — and with it every failed file
  // and the only route to retrying them. Ask the server whose import this is.
  useEffect(() => {
    if (tenderImportId || !projectId) return undefined;

    let cancelled = false;
    (async () => {
      try {
        const { fetchImportForFolder } = await import("../../../../services/tendersApi");
        const job = await fetchImportForFolder(projectId);
        if (cancelled || !job?.id) return;
        setImportJob(job);
        setTenderImportId(job.id);
        // Seed the seen count so the first poll of an already-finished import
        // does not trigger a second, pointless refresh of the file list.
        importDoneCountRef.current = job.counts?.done ?? -1;
      } catch {
        // Most projects were never imported. That is not a problem to report.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenderImportId, projectId]);

  useEffect(() => {
    if (!tenderImportId || !projectId) return undefined;

    let cancelled = false;
    let timer = null;
    // Consecutive polls that saw no change. A job whose worker or broker died
    // never leaves "pending", and this used to ask every three seconds for as
    // long as the tab stayed open, with nothing on screen to say so.
    let idle = 0;
    let lastCounts = "";
    setImportStalled(false);

    const poll = async () => {
      try {
        const { fetchImportStatus } = await import("../../../../services/tendersApi");
        const job = await fetchImportStatus(tenderImportId);
        if (cancelled) return;
        // The captured id survives redirects on purpose; make sure it still
        // belongs to the project on screen before showing anything for it.
        if (job.folder_id && String(job.folder_id) !== String(projectId)) {
          setImportJob(null);
          return;
        }
        setImportJob(job);

        if (job.counts && job.counts.done !== importDoneCountRef.current) {
          importDoneCountRef.current = job.counts.done;
          fetchFiles(true);
        }

        const counts = JSON.stringify(job.counts || {});
        if (counts === lastCounts) idle += 1;
        else {
          idle = 0;
          lastCounts = counts;
        }

        if (["pending", "running"].includes(job.status)) {
          watchedImportRunningRef.current = true;
          // Three seconds while it is moving. Once it stops moving, back off,
          // and after about eleven minutes of nothing stop asking and say so,
          // rather than polling an dead job behind a forgotten tab.
          if (idle >= 60) {
            setImportStalled(true);
            return;
          }
          timer = setTimeout(poll, idle < 20 ? 3000 : 15000);
          return;
        }

        // Reporting the outcome only makes sense to someone who was here for
        // it. Without this, opening a long-finished project would announce its
        // import all over again, every single time.
        if (!watchedImportRunningRef.current) return;

        fetchFiles(true);
        if (job.status === "completed") {
          const n = job.counts?.done ?? 0;
          if (n > 0) {
            showToast("success", `${n} document${n === 1 ? "" : "s"} imported from the tender`);
          }
        } else if (job.status === "completed_no_documents") {
          // Nothing failed and nothing arrived. A toast auto-dismisses, and
          // this needs a decision rather than an acknowledgement: the project
          // cannot analyse anything until a document is in it. The guard above
          // means only someone who watched the import run sees this, so
          // reopening the project later does not repeat it.
          setNoDocsNotice(job.error || "We found nothing to download for this tender.");
        } else if (job.status === "completed_with_errors") {
          showToast("warning", job.error || "Some tender documents could not be downloaded.");
        } else if (job.status === "failed") {
          showToast("error", job.error || "The tender documents could not be downloaded.");
        }
      } catch (err) {
        if (cancelled) return;
        // A transient status failure shouldn't kill the poll; a 404 should.
        if (err?.response?.status !== 404) timer = setTimeout(poll, 5000);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [tenderImportId, projectId, fetchFiles, showToast, importWatch]);

  // Retry is job-level, not per-file: the endpoint requeues everything that
  // genuinely failed and leaves skips alone, so one button is the honest
  // affordance. Every outcome resyncs the job rather than guessing at it.
  const retryTenderImport = useCallback(async () => {
    if (!tenderImportId || retryingImport) return;
    setRetryingImport(true);
    try {
      const { retryImport } = await import("../../../../services/tendersApi");
      const job = await retryImport(tenderImportId);
      setImportJob(job);
      importDoneCountRef.current = -1;
      setImportWatch((n) => n + 1);
      showToast("success", "Retrying the documents that failed");
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) {
        setImportJob(null);
        setTenderImportId(null);
      } else {
        showToast(
          code === 409 || code === 400 ? "warning" : "error",
          err?.response?.data?.error || "Could not retry the import."
        );
        // 409 means it is running after all and 400 means nothing failed after
        // all; either way this screen is behind, so look again.
        if (code === 409 || code === 400) setImportWatch((n) => n + 1);
      }
    } finally {
      setRetryingImport(false);
    }
  }, [tenderImportId, retryingImport, showToast]);

  // While the import runs, its files appear as rows in the same table an
  // upload uses — the mechanism users already know — with statuses the table
  // already renders (queued spinner, downloading spinner, red failed). Rows
  // for files still on their way; once one lands, the silent refresh replaces
  // it with the real document row. Skips (restricted, links, unsupported)
  // never become documents, so they get the closing toast, not a fake row.
  const tenderImportPlaceholders = useMemo(() => {
    if (!importJob) return [];
    const running = ["pending", "running"].includes(importJob.status);
    // Keys of documents the backend list already contains. A "done" file keeps
    // its placeholder until its real row is in here — dropping it on the poll
    // and adding the row on the (later) fetch left a frame where the file was
    // in neither list, which read as the file flashing out of the table.
    const backendKeys = new Set(
      (files.documents || []).flatMap((d) => [d.key, d.pdf_key, d.id].filter(Boolean))
    );
    const now = new Date();
    const stamp = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;
    return (importJob.files || [])
      .filter((f) => {
        // The done-placeholder only exists to bridge the frame between a file
        // finishing and its real row arriving, so it is only honest while we
        // are actually watching the job. On a later visit a done file missing
        // from the backend list was deleted by someone, not still landing.
        if (f.status === "done") {
          return watchedImportRunningRef.current
            && f.pdf_key && !backendKeys.has(f.pdf_key);
        }
        // A failed file stays on the table after the job ends. It used to be
        // dropped the moment the job settled, so the only trace of a missing
        // document was one toast — miss it and the file was gone silently.
        if (f.status === "failed") return true;
        // Still-queued rows only mean something while the job is alive.
        return running && ["pending", "downloading"].includes(f.status);
      })
      .map((f) => ({
        id: `tender-import-file-${f.id}`,
        name: f.name || "Attachment",
        status:
          f.status === "pending" ? "queued"
          : f.status === "done" ? "processing"
          : f.status,
        uploaded_at: stamp,
        file_type: (f.name || "").includes(".")
          ? f.name.split(".").pop().toLowerCase()
          : "pdf",
        uploaded_by: "SAM.gov",
        tags: [],
        isTenderImport: true,
      }));
  }, [importJob, files.documents]);

  useEffect(() => {
    const hasProcessingFiles = files.documents?.some((doc) => {
      const s = doc.status?.toLowerCase();
      return ["processing", "uploading", "pending", "extracting"].includes(s);
    });

    if (hasProcessingFiles) {
      const intervalId = setInterval(() => {
        const pollUrl = orgId
          ? `api/v1/rfp/folders/${projectId}/files/?organization_id=${encodeURIComponent(orgId)}`
          : `api/v1/rfp/folders/${projectId}/files/`;
        api
          .get(pollUrl)
          .then((response) => {
            if (response.status === 200) {
              // FIX 3: Save additional_tasks during polling
              setFiles({
                documents: response.data.documents || [],
                folder_name: response.data.folder_name,
                proposal_available: response.data.proposal_available,
                additional_tasks: response.data.additional_tasks || [],
                analysis_summary: response.data.analysis_summary,
                user_permission: response.data.user_permission, // keep permission during polling so reviewer gates don't reset
                is_owner: response.data.is_owner,
                rfp_status: response.data.rfp_status,
              });
            }
          })
          .catch((error) => console.error("Background polling error:", error));
      }, 3000);

      return () => clearInterval(intervalId);
    }
  }, [files.documents, projectId, orgId]);

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) return;
    setFilesToUpload(selected);
    setIsUploadModalOpen(true);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Chat with the project in Sensa: a new conversation with this project pinned
  // and its documents open beside it (see RfpCopilotPage, chatWithProject).
  const navigateToProjectChat = () => {
    navigate("/rfp-copilot", {
      state: { chatWithProject: { projectId, projectName: files.folder_name } },
    });
  };

  const handleViewProposal = async () => {
    if (!projectId) return showToast("error", "Project ID not found");
    setIsGeneratingProposal(true);

    try {
      const token = localStorage.getItem("token");
      const response = await api.post(
        `api/v1/rfp/folders/${projectId}/generate-proposal/`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if ([200, 201, 202].includes(response.status)) {
        showToast("success", "Proposal generated successfully!");
        const navState = {
          proposalData: response.data,
          projectName: files.folder_name || folderDetails.name,
          folderId: projectId,
          orgId: orgId || null,
        };

        if (projectId && orgId)
          navigate(
            `/rfp-projects/project/${projectId}/org/${orgId}/proposal/analysis`,
            { state: navState },
          );
        else
          navigate(`/rfp-projects/project/${projectId}/proposal/analysis`, {
            state: navState,
          });
      }
    } catch (error) {
      console.error("Generate proposal error:", error);
      showToast(
        "error",
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to generate proposal",
      );
    } finally {
      setIsGeneratingProposal(false);
    }
  };

  const handleCreateProposal = async () => {
    if (!files.documents?.length) {
      showToast("error", "Please upload documents before creating a proposal");
      return;
    }

    const isProcessing = files.documents.some((doc) => {
      const s = doc.status?.toLowerCase();
      return ["processing", "uploading", "pending", "extracting"].includes(s);
    });

    const isManaging = files.proposal_available;

    if (!isManaging && isProcessing) {
      showToast(
        "warning",
        "Please wait for all documents to finish processing.",
      );
      return;
    }

    // No API call here — just navigate to the proposal page. That page owns the
    // proposal-config fetch and shows its own loading state on mount.
    setIsCreateManageDropdownOpen(false);
    navigate(`/rfp-projects/project/${projectId}/org/${orgId}/proposal`, {
      state: {
        projectName: files.folder_name || folderDetails.name,
        folderId: projectId,
      },
    });
  };

  const handleAnswerQuestionnaire = () => {
    setIsAnsweringQuestionnaire(true);
    setTimeout(() => {
      navigate(`/rfp-projects/project/${projectId}/org/${orgId}/questionnaire`);
      setIsAnsweringQuestionnaire(false);
      setIsCreateManageDropdownOpen(false);
    }, 800);
  };

  const handleFillForms = () => {
    setIsFillingForms(true);
    setTimeout(() => {
      navigate(`/rfp-projects/project/${projectId}/org/${orgId}/forms`);
      setIsFillingForms(false);
      setIsCreateManageDropdownOpen(false);
    }, 800);
  };

  // --- Analysis jobs (driven by the always-mounted AnalysisWatcherHost) ---
  // The bid-readiness and requirements pipelines no longer run inside this page.
  // ProjectPage only STARTS a job; the global watcher issues every request,
  // polls to completion, and toasts when done — so the work survives navigation.
  // Here we derive the in-progress flags from the job store (so the processing
  // popup shows during the run AND when the user returns mid-run) and refresh
  // the rendered results when a job ends.
  const folderJobs = useFolderJobs(projectId);
  const bidJob = folderJobs.find((j) => j.kind === "bid-readiness") || null;
  const reqJob = folderJobs.find((j) => j.kind === "requirements") || null;
  const screeningJob = folderJobs.find((j) => j.kind === "screening") || null;
  const isAnalyzing = !!bidJob;
  const screeningAnalyzing = !!screeningJob;
  // `reqFinishing` bridges the gap between the requirements job ending and the
  // alignment result being (re)loaded, so the Run Analysis prompt never flashes
  // between "processing" and "results". `alignmentLoaded` does the same for the
  // very first load when landing on the tab with an already-finished analysis.
  const [reqFinishing, setReqFinishing] = useState(false);
  const [alignmentLoaded, setAlignmentLoaded] = useState(false);
  const requirementAnalyzing = !!reqJob || reqFinishing;

  const runBidReadinessAnalysis = useCallback(() => {
    if (!projectId) return showToast("error", "Project ID not found");
    if (getJob(projectId, "bid-readiness")) return; // already running
    if (!files.documents?.length) {
      return showToast(
        "error",
        "Please upload documents before running analysis",
      );
    }
    setIsViewAnalysisOpen(false);
    startJob({
      folderId: projectId,
      orgId,
      folderName: files.folder_name,
      kind: "bid-readiness",
      phase: "summarizing",
    });
    showToast(
      "success",
      "Analysis started — you can leave this page; we'll notify you when it's ready.",
    );
  }, [projectId, orgId, files.documents, files.folder_name, showToast]);

  const runRequirementAnalysis = useCallback(() => {
    if (!projectId) return showToast("error", "Project ID not found");
    if (getJob(projectId, "requirements")) return; // already running
    if (!files.documents?.length) {
      return showToast(
        "error",
        "Please upload documents before running analysis",
      );
    }
    // Re-analysis: if requirements were already extracted, leave those
    // documents alone and extract only the ones without any (e.g. a file added
    // since). Extraction regenerates requirement ids and replaces the insights
    // blob, which would wipe member assignments on the documents already done.
    const alreadyExtracted = (alignmentResult?.documents || []).some((d) =>
      Object.values(d.requirements_by_category || {}).some(
        (arr) => Array.isArray(arr) && arr.length > 0,
      ),
    );
    setIsViewAnalysisOpen(false);
    startJob({
      folderId: projectId,
      orgId,
      folderName: files.folder_name,
      kind: "requirements",
      phase: "extracting",
      extractNewOnly: alreadyExtracted,
    });
    showToast(
      "success",
      "Analysis started — you can leave this page; we'll notify you when it's ready.",
    );
  }, [
    projectId,
    orgId,
    files.documents,
    files.folder_name,
    showToast,
    alignmentResult,
  ]);

  // Screening (the "Generate Answers" action inside Requirements) runs as its own
  // background job too, so answers keep generating after navigation and toast on
  // completion — same mechanism as the other flows.
  const runScreeningAnalysis = useCallback(() => {
    if (!projectId) return showToast("error", "Project ID not found");
    if (getJob(projectId, "screening")) return; // already running
    startJob({
      folderId: projectId,
      orgId,
      folderName: files.folder_name,
      kind: "screening",
      phase: "screening",
    });
    showToast(
      "success",
      "Generating answers — you can leave this page; we'll notify you when it's ready.",
    );
  }, [projectId, orgId, files.folder_name, showToast]);

  // --- Requirement alignment result (read-only here; the watcher drives it) ---
  const fetchAlignment = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await api.get(
        orgId
          ? `api/v1/rfp/folders/${projectId}/requirements/alignment/?organization_id=${encodeURIComponent(orgId)}`
          : `api/v1/rfp/folders/${projectId}/requirements/alignment/`,
      );
      const data = res.data || {};

      // ── Alignment STATUS — updated on every poll. The "running" early-return
      //    carries only the status (no documents), which is exactly why status
      //    and the requirements mapping are tracked independently here.
      // "extraction_pending": alignment was asked for and the server is holding
      // it until every document has extracted — it starts by itself after that.
      const s = data.alignment_status || data.status;
      setAlignmentStatus(
        ["done", "failed", "running", "extraction_pending", "not_started"].includes(s)
          ? s
          : "not_started",
      );

      // ── REQUIREMENTS mapping — updated only when the response actually carries
      //    documents. The running response omits them, so the already-mapped
      //    table persists instead of collapsing back to the spinner. (The
      //    extraction_pending response does carry them, each with its own
      //    `extraction` progress, which is what the per-file bars draw.)
      if (Array.isArray(data.documents) && data.documents.length > 0) {
        setAlignmentResult(data);
      }
    } catch (error) {
      // Keep the last known status on a transient failure. Resetting to
      // "not_started" here would collapse a "done" results view back to the Run
      // Analysis prompt on a single failed poll (fetchAlignment is polled while
      // screening runs). The initial state is already "not_started".
    } finally {
      setAlignmentLoaded(true);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) fetchAlignment();
  }, [projectId, fetchAlignment]);

  // Refresh the rendered results the moment a job finishes (present -> absent):
  // bid-readiness pulls fresh dates from files; requirements reloads alignment
  // while keeping the processing screen up until the result lands (no flash).
  const prevBidJobRef = useRef(false);
  const prevReqJobRef = useRef(false);

  // ProjectPage is REUSED when navigating folder -> folder (same route), so its
  // state would otherwise carry over. Reset all per-folder analysis view state on
  // projectId change so the new folder never briefly shows the previous folder's
  // alignment results (the fetchAlignment effect below then reloads the real one).
  useEffect(() => {
    setAlignmentLoaded(false);
    setAlignmentStatus("not_started");
    setAlignmentResult(null);
    setReqFinishing(false);
    prevBidJobRef.current = !!getJob(projectId, "bid-readiness");
    prevReqJobRef.current = !!getJob(projectId, "requirements");
  }, [projectId]);

  useEffect(() => {
    if (prevBidJobRef.current && !bidJob) fetchFiles(true);
    prevBidJobRef.current = !!bidJob;
  }, [bidJob, fetchFiles]);

  // While a bid-readiness job runs, refresh files so the tab maps dates/overview
  // progressively and shows the in-progress banner (the watcher polls its own
  // requests, which don't touch this page's state). Stops when the job ends.
  useEffect(() => {
    if (!bidJob) return;
    const id = setInterval(() => fetchFiles(true), 3000);
    return () => clearInterval(id);
  }, [bidJob, fetchFiles]);
  useEffect(() => {
    if (prevReqJobRef.current && !reqJob) {
      setReqFinishing(true);
      fetchAlignment().finally(() => setReqFinishing(false));
    }
    prevReqJobRef.current = !!reqJob;
  }, [reqJob, fetchAlignment]);

  // A document still extracting outside any job: a per-file Resume during a
  // run, or one started from another tab. The server's own liveness marker
  // decides queued/running, and it expires, so this can never poll for ever.
  const extractionInFlight = useMemo(
    () =>
      (alignmentResult?.documents || []).some((d) =>
        ["queued", "running"].includes(d.extraction?.state),
      ),
    [alignmentResult],
  );

  // While the requirements job runs, poll alignment so extracted requirements
  // stream into the results table (mapped progressively) and the alignment
  // status updates — the table renders as soon as requirements exist, while the
  // "Analysing" indicator stays up until alignment_status flips to "done". The
  // per-file progress bars read the same response.
  useEffect(() => {
    if (!reqJob && !extractionInFlight) return;
    const id = setInterval(() => fetchAlignment(), 3000);
    return () => clearInterval(id);
  }, [reqJob, extractionInFlight, fetchAlignment]);

  // Re-extract the given documents. Each resumes after the last step it
  // finished, keeping its finished work (and what people did to its
  // requirements) — see insights_dispatch.enqueue_insight_extraction.
  const resumeExtraction = useCallback(
    async (keys) => {
      if (!projectId || !keys?.length) return;
      const n = keys.length === 1 ? "1 file" : `${keys.length} files`;
      if (getJob(projectId, "requirements")) {
        // A run is still going: put these documents back into it. Its alignment
        // is held until every document has extracted, so it waits for them too.
        try {
          await api.post(
            `api/v1/rfp/folders/${projectId}/insights/extract/?insights=requirements` +
              keys.map((k) => `&doc_keys=${encodeURIComponent(k)}`).join(""),
          );
          showToast("success", `Resuming ${n}`);
          fetchAlignment();
        } catch {
          showToast("error", `Couldn't resume ${n}`);
        }
        return;
      }
      // Otherwise a run of its own, so alignment is redone with the new
      // requirements once they land — same as Run Analysis, for these files only.
      startJob({
        folderId: projectId,
        orgId,
        folderName: files.folder_name,
        kind: "requirements",
        phase: "extracting",
        extractKeys: keys,
      });
      showToast("success", `Resuming ${n} — you can leave this page; we'll notify you when it's ready.`);
    },
    [projectId, orgId, files.folder_name, showToast, fetchAlignment],
  );

  // True once at least one document has extracted requirements, regardless of
  // whether the alignment (scoring) step has finished.
  const requirementsExtracted = useMemo(() => {
    const docs = alignmentResult?.documents || [];
    return docs.some((d) => {
      const cats = d.requirements_by_category || {};
      return Object.values(cats).some(
        (arr) => Array.isArray(arr) && arr.length > 0,
      );
    });
  }, [alignmentResult]);

  // While screening runs, poll alignment so answers stream into the results
  // view; reload once more when it ends so the final answers/state are shown.
  const prevScreeningJobRef = useRef(false);
  useEffect(() => {
    if (screeningJob) {
      const id = setInterval(() => fetchAlignment(), 3000);
      return () => clearInterval(id);
    }
    if (prevScreeningJobRef.current && !screeningJob) fetchAlignment();
  }, [screeningJob, fetchAlignment]);
  useEffect(() => {
    prevScreeningJobRef.current = !!screeningJob;
  }, [screeningJob]);

  const requirementAnalysisReady = alignmentStatus === "done";

  return (
    <div className="flex flex-col h-screen w-full bg-[#FEF9F7] overflow-hidden font-outfit">
      <div className="flex-shrink-0 bg-white">
        <Header
          icon={"/arrow-back.svg"}
          name={
            <div className="flex items-center gap-2">
              <FolderSvg />
              <span
                className="truncate max-w-[400px]"
                title={files.folder_name || folderDetails.name}
              >
                {files.folder_name || folderDetails.name || "Fetching..."}
              </span>
              <HoverTooltip content="Audit Logs">
              <button
                type="button"
                aria-label="Project activity log"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setActivityOpen(true);
                }}
                className="ml-1 w-8 h-8 flex items-center justify-center rounded-md bg-[#B482FF33] border border-[#8B55DDB2] text-[#8B55DD]  transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  fill="none"
                >
                  <path
                    d="M8.25 16.5C7.10875 16.5 6.03625 16.2834 5.0325 15.8503C4.02875 15.4172 3.15562 14.8294 2.41313 14.0869C1.67062 13.3444 1.08281 12.4713 0.649688 11.4675C0.216562 10.4637 0 9.39125 0 8.25C0 7.10875 0.216562 6.03625 0.649688 5.0325C1.08281 4.02875 1.67062 3.15562 2.41313 2.41313C3.15562 1.67062 4.02875 1.08281 5.0325 0.649688C6.03625 0.216562 7.10875 0 8.25 0C9.405 0 10.4809 0.216562 11.4778 0.649688C12.4747 1.08281 13.3444 1.67062 14.0869 2.41313C14.8294 3.15562 15.4172 4.02531 15.8503 5.02219C16.2834 6.01906 16.5 7.095 16.5 8.25C16.5 8.3875 16.4966 8.52156 16.4897 8.65219C16.4828 8.78281 16.4725 8.91 16.4587 9.03375C16.4312 9.2675 16.3316 9.44625 16.1597 9.57C15.9878 9.69375 15.785 9.735 15.5513 9.69375C15.3175 9.6525 15.1319 9.53562 14.9944 9.34312C14.8569 9.15062 14.8019 8.9375 14.8294 8.70375C14.8294 8.635 14.8328 8.55938 14.8397 8.47688C14.8466 8.39438 14.85 8.31875 14.85 8.25C14.85 7.32875 14.6816 6.46938 14.3447 5.67188C14.0078 4.87437 13.5369 4.17313 12.9319 3.56813C12.3269 2.96313 11.6256 2.49219 10.8281 2.15531C10.0306 1.81844 9.17125 1.65 8.25 1.65C6.4075 1.65 4.84688 2.28938 3.56813 3.56813C2.28938 4.84688 1.65 6.4075 1.65 8.25C1.65 10.0925 2.28938 11.6531 3.56813 12.9319C4.84688 14.2106 6.4075 14.85 8.25 14.85C8.86875 14.85 9.46344 14.7709 10.0341 14.6128C10.6047 14.4547 11.1444 14.2175 11.6531 13.9012C11.8456 13.7913 12.0553 13.75 12.2822 13.7775C12.5091 13.805 12.6844 13.915 12.8081 14.1075C12.9456 14.3 12.9903 14.5097 12.9422 14.7366C12.8941 14.9634 12.7737 15.1388 12.5813 15.2625C11.935 15.6613 11.2509 15.9672 10.5291 16.1803C9.80719 16.3934 9.0475 16.5 8.25 16.5ZM13.4991 12.9009C13.2997 12.7016 13.2 12.4575 13.2 12.1687C13.2 11.88 13.2997 11.6359 13.4991 11.4366C13.6984 11.2372 13.9425 11.1375 14.2312 11.1375C14.52 11.1375 14.7641 11.2372 14.9634 11.4366C15.1628 11.6359 15.2625 11.88 15.2625 12.1687C15.2625 12.4575 15.1628 12.7016 14.9634 12.9009C14.7641 13.1003 14.52 13.2 14.2312 13.2C13.9425 13.2 13.6984 13.1003 13.4991 12.9009ZM9.075 7.92L11.55 10.395C11.7013 10.5463 11.7769 10.7388 11.7769 10.9725C11.7769 11.2063 11.7013 11.3988 11.55 11.55C11.3988 11.7013 11.2063 11.7769 10.9725 11.7769C10.7388 11.7769 10.5463 11.7013 10.395 11.55L7.6725 8.8275C7.59 8.745 7.52812 8.65219 7.48687 8.54906C7.44562 8.44594 7.425 8.33938 7.425 8.22937V4.95C7.425 4.71625 7.50406 4.52031 7.66219 4.36219C7.82031 4.20406 8.01625 4.125 8.25 4.125C8.48375 4.125 8.67969 4.20406 8.83781 4.36219C8.99594 4.52031 9.075 4.71625 9.075 4.95V7.92Z"
                    fill="#8B55DD"
                  />
                </svg>
              </button>
              </HoverTooltip>
            </div>
          }
          onClick={() => navigate(`/rfp-projects/my-projects`)}
          isProjectPage={true}
          projectData={{
            files,
            isGenerating,
            isAnsweringQuestionnaire,
            isFillingForms,
            isGeneratingProposal,
            isLoading: tableLoading,
            isProcessing: files.documents?.some((doc) =>
              ["processing", "uploading", "pending", "extracting"].includes(
                doc.status?.toLowerCase(),
              ),
            ),
            isUploadIngestBusy:
              isTabBusy ||
              files.documents?.some((doc) =>
                [
                  "uploading",
                  "extracting",
                  "processing",
                  "pending",
                  "queued",
                  "ingesting",
                ].includes(doc.status?.toLowerCase()),
              ),
            // "View Analysis" shows on Requirements + Bid Readiness once the
            // requirement alignment analysis has completed.
            analysisReady:
              requirementAnalysisReady &&
              (activeTab === "requirements" || activeTab === "bid-readiness"),
            // While the current tab's analysis is running, the "View Analysis"
            // button shows a loading state instead.
            analysisLoading:
              (activeTab === "bid-readiness" && isAnalyzing) ||
              (activeTab === "requirements" && requirementAnalyzing),
          }}
          projectHandlers={{
            onChat: navigateToProjectChat,
            onViewProposal: handleViewProposal,
            onCreateProposal: handleCreateProposal,
            onAnswerQuestionnaire: handleAnswerQuestionnaire,
            onFillForms: handleFillForms,
            onViewAnalysis: (e) => {
              const rect = e?.currentTarget?.getBoundingClientRect?.();
              if (rect) {
                setAnalysisAnchor({
                  top: rect.bottom + 8,
                  right: Math.max(12, window.innerWidth - rect.right),
                });
              }
              setIsViewAnalysisOpen(true);
            },
            onUploadTrigger: () => {
              if (tabUploadHandler) tabUploadHandler();
              else if (fileInputRef.current) fileInputRef.current.click();
            },
          }}
          isCreateManageDropdownOpen={isCreateManageDropdownOpen}
          setIsCreateManageDropdownOpen={setIsCreateManageDropdownOpen}
          createManageDropdownRef={createManageDropdownRef}
          subscriptionChecked={subscriptionChecked}
          isPaidYearlyPlan={isPaidYearlyPlan}
        />
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        accept=".pdf,.xls,.xlsx,.csv,.txt,.doc,.jpeg,.jpg,.png"
        onChange={handleFileChange}
      />

      <div className="flex flex-col flex-1 overflow-hidden w-full bg-white border-t border-black/10">
        <div
          className={`flex justify-between items-center px-6 flex-shrink-0 w-full border-b ${activeTab === "bid-readiness" || "requirements" ? "h-[50px] pt-6" : "h-[50px]"}`}
        >
          <TabLayout activeTab={activeTab} setActiveTab={handleTabChange} />
          <div
            key={`controls-${activeTab}`}
            id="project-tab-controls-portal"
            className="flex items-center"
          ></div>
        </div>

        <div className="flex-1 flex flex-col overflow-hidden bg-[#fafafa]">
          {/* FIX 4: Pass additional_tasks down to the Outlet */}
          <Outlet
            key={activeTab}
            context={{
              files: tenderImportPlaceholders.length
                ? [...tenderImportPlaceholders, ...(files.documents || [])]
                : files.documents || [],
              tenderImport: importJob,
              retryTenderImport,
              retryingImport,
              importStalled,
              projectName: files.folder_name || folderDetails.name,
              analysis_summary: files.analysis_summary,
              rfp_status: files.rfp_status,
              additional_tasks: files.additional_tasks || [],
              loading: tableLoading,
              fetchFiles,
              projectId,
              orgId,
              showToast,
              setTabUploadHandler,
              setIsTabBusy,
              runBidReadinessAnalysis,
              isAnalyzing,
              analysisReady,
              runRequirementAnalysis,
              requirementAnalyzing,
              requirementAnalysisReady,
              requirementsExtracted,
              requirementStatusKnown: alignmentLoaded,
              runScreeningAnalysis,
              screeningAnalyzing,
              alignmentResult,
              fetchAlignment,
              resumeExtraction,
            }}
          />
        </div>

        <div
          key={`footer-${activeTab}`}
          id="project-tab-footer-portal"
          className="w-full bg-white border-t border-black/10 flex-shrink-0 z-10 empty:hidden"
        ></div>
      </div>

      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => {
          setIsUploadModalOpen(false);
          setFilesToUpload([]);
        }}
        filesToUpload={filesToUpload}
        folderId={projectId}
        onUploadSuccess={fetchFiles}
        uploadEndpoint={`api/v1/rfp/folders/${projectId}/upload/`}
        modalTitle="Uploading to Project"
      />

      <BidReadinessAnalysisModal
        isOpen={isViewAnalysisOpen}
        onClose={() => setIsViewAnalysisOpen(false)}
        anchor={analysisAnchor}
        result={alignmentResult}
        onRerun={runRequirementAnalysis}
        isAnalyzing={alignmentStatus === "running" || alignmentStatus === "extraction_pending"}
      />

      <GenerationLoadingModal
        isOpen={isGenerating && !files.proposal_available}
        currentPhase={generationPhase}
      />

      <ProjectActivityLog
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        folderId={projectId}
      />

      {/* The import ran and brought in nothing. Offer the one action that
          unblocks the project rather than leaving the user to work out that
          analysis is waiting on a document. */}
      {noDocsNotice && (
        <ConfirmDialog
          title="Project created, but no documents came in"
          // The job's reason is already specific and already ends by saying what
          // to do. Appending our own sentence put a second call to action ahead
          // of the explanation, with an Upload button sitting right below both.
          message={noDocsNotice}
          tip="Bid Readiness, Requirements and Chat stay unavailable until at least one document is in the project."
          confirmLabel="Upload documents"
          cancelLabel="Later"
          onCancel={() => setNoDocsNotice(null)}
          onConfirm={() => {
            setNoDocsNotice(null);
            if (tabUploadHandler) tabUploadHandler();
            else if (fileInputRef.current) fileInputRef.current.click();
          }}
        />
      )}
    </div>
  );
};

export default ProjectPage;
